import { useMemo } from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import DynamicHighchart from '~/components/atoms/DynamicHighchart/DynamicHighchart'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Loader from '~/components/atoms/Loader/Loader'
import PortfolioLozenge from '~/components/atoms/PortfolioLozenge/PortfolioLozenge'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import type { CollectionChartData } from '~/types/collectionChart'

import useCollectionChart, { FilterType } from './hooks'

import styles from './CollectionValueChart.module.css'

const formatValueLabel = (value: number): string => {
  if (value >= 1000000) {
    const millions = value / 1000000
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(2)}M`
  }
  if (value >= 1000) {
    const thousands = value / 1000
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(0)}K`
  }
  return value.toString()
}

export interface CollectionValueChartProps {
  data: CollectionChartData | null
  collectionId: string
  testID?: string
  title?: string
  summary?: any
  theme?: ComponentTheme
  moduleId?: string
}

export default function CollectionValueChart({
  data: initialData,
  collectionId,
  testID,
  title,
  summary,
  theme = ComponentTheme.primary,
  moduleId,
}: CollectionValueChartProps) {
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const t = useTranslations('collectionAuctionChart')

  const { chartData, currentFilter, setCurrentFilter, loading, error } =
    useCollectionChart({
      collectionId,
      initialData,
    })

  const chartOptions = useMemo(() => {
    if (!chartData) return null

    return {
      chart: {
        type: 'column',
        height: isMobile ? 280 : 400,
        backgroundColor: undefined,
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories: chartData.chart_data.periods,
        crosshair: false,
        labels: {
          style: {
            fontSize: '12px',
            color: '#666',
          },
        },
      },
      yAxis: {
        title: {
          text: undefined,
        },
        labels: {
          enabled: false,
        },
        gridLineColor: '#e6e6e6',
      },
      tooltip: {
        enabled: false,
      },
      plotOptions: {
        column: {
          animation: false,
          maxPointWidth: 40,
          dataLabels: {
            enabled: true,
            formatter: function (this: any) {
              return formatValueLabel(this.y)
            },
            style: {
              fontSize: '11px',
              fontWeight: '600',
              color: '#000',
              textOutline: 'none',
            },
          },
          color: '#B3A0E7',
          borderRadius: 0,
          pointPadding: 0.1,
          groupPadding: 0.15,
        },
        series: {
          states: {
            hover: {
              enabled: false,
            },
            inactive: {
              enabled: false,
            },
          },
        },
      },
      series: [
        {
          name: chartData.collection_name,
          data: chartData.chart_data.hammer_values,
        },
      ],
    }
  }, [chartData, isMobile])

  if (!chartData && !loading) {
    return null
  }

  return (
    <div
      className={classNames(styles.base, theme && styles[theme])}
      data-testid={testID}
    >
      <Fixture theme={theme} verticalPadding id={moduleId}>
        <Container>
          <div className={styles.head}>
            {title && (
              <Text
                Tag="h3"
                size={TextSizes.primaryTitleLrg}
                className={styles.title}
              >
                {title}
              </Text>
            )}
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} />
                <Text size={TextSizes.bodySml}>{t('annualSales')}</Text>
              </div>
            </div>
          </div>

          <div className={styles.filterContainer}>
            <PortfolioLozenge
              title={t('all')}
              isActive={currentFilter === FilterType.all}
              onClick={() => setCurrentFilter(FilterType.all)}
              disabled={loading}
            />
            <PortfolioLozenge
              title={t('tenYear')}
              isActive={currentFilter === FilterType.tenYear}
              onClick={() => setCurrentFilter(FilterType.tenYear)}
              disabled={loading}
            />
            <PortfolioLozenge
              title={t('fiveYear')}
              isActive={currentFilter === FilterType.fiveYear}
              onClick={() => setCurrentFilter(FilterType.fiveYear)}
              disabled={loading}
            />
            <PortfolioLozenge
              title={t('twelveMonths')}
              isActive={currentFilter === FilterType.twelveMonths}
              onClick={() => setCurrentFilter(FilterType.twelveMonths)}
              disabled={loading}
            />
          </div>

          {error && (
            <div className={styles.error}>
              <Text>
                {t('errorLoadingChart')}: {error}
              </Text>
            </div>
          )}

          <div
            className={classNames(styles.chartContainer, {
              [styles.loading]: loading,
            })}
          >
            {loading && (
              <div className={styles.loadingOverlay}>
                <Loader />
              </div>
            )}
            {chartOptions && (
              <DynamicHighchart
                key={`${currentFilter}-${chartData?.chart_data.periods.length}`}
                options={chartOptions}
              />
            )}
          </div>
        </Container>
      </Fixture>
    </div>
  )
}
